import React, { useState, useEffect } from 'react'
import { BarChart3, TrendingUp, TrendingDown, Settings } from 'lucide-react'

import { useForm } from '../../hooks/useForm'
import { useToast } from '../Toast'
import Button from '../ui/Button'
import Input from '../ui/Input'
import Select from '../ui/Select'
import TextArea from '../ui/Textarea'

import { Produto } from '../../types'
import movimentacoesService, { 
  MovimentacaoFormData,
  tiposMovimentacao,
  getTipoMovimentacaoConfig,
  validarQuantidadeMovimentacao
} from '../../services/movimentacoes'
import { 
  formatarQuantidade,
  getStatusEstoque,
  formatarValor
} from '../../services/produtos'

interface MovimentacaoFormProps {
  produto: Produto
  onSuccess: () => void
  onCancel: () => void
}

const MovimentacaoForm: React.FC<MovimentacaoFormProps> = ({
  produto,
  onSuccess,
  onCancel
}) => {
  const { success, error } = useToast()
  
  // Estados locais
  const [tipoSelecionado, setTipoSelecionado] = useState<'ENTRADA' | 'SAIDA' | 'AJUSTE' | ''>('')
  const [novoEstoque, setNovoEstoque] = useState(0)
  const [isValidating, setIsValidating] = useState(false)

  // Form
  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    setFieldError,
    clearFieldError
  } = useForm({
    initialValues: {
      tipo: '' as 'ENTRADA' | 'SAIDA' | 'AJUSTE' | '',
      quantidade: 0,
      observacoes: ''
    },
    validationRules: {
      tipo: {
        required: 'Tipo de movimentação é obrigatório'
      },
      quantidade: {
        required: 'Quantidade é obrigatória',
        min: { value: 1, message: 'Quantidade deve ser maior que zero' },
        validate: (value: number) => {
          const validacao = validarQuantidadeMovimentacao(value, values.tipo)
          return validacao.valida || validacao.erro!
        }
      },
      observacoes: {
        required: 'Observações são obrigatórias'
      }
    },
    onSubmit: async (formValues) => {
      try {
        const data: MovimentacaoFormData = {
          produtoId: produto.id,
          tipo: formValues.tipo,
          quantidade: formValues.quantidade,
          observacoes: formValues.observacoes
        }

        // Validar antes de enviar
        const validacao = await movimentacoesService.validarMovimentacao(data)
        
        if (!validacao.valida) {
          error('Erro de validação', validacao.erro!)
          return
        }

        if (data.tipo === 'AJUSTE') {
          // Para ajustes, usar endpoint específico
          await movimentacoesService.ajustarEstoque({
            produtoId: produto.id,
            novoEstoque: data.quantidade,
            observacoes: data.observacoes!
          })
        } else {
          // Para entradas e saídas normais
          await movimentacoesService.create(data)
        }
        
        success('Movimentação registrada com sucesso')
        onSuccess()
      } catch (err: any) {
        error('Erro ao registrar movimentação', err.message)
      }
    }
  })

  // Efeitos
  useEffect(() => {
    if (values.tipo && values.quantidade > 0) {
      calcularNovoEstoque()
    }
  }, [values.tipo, values.quantidade])

  // Handlers
  const handleTipoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tipo = e.target.value as 'ENTRADA' | 'SAIDA' | 'AJUSTE' | ''
    setTipoSelecionado(tipo)
    setFieldValue('tipo', tipo)
    setFieldValue('quantidade', tipo === 'AJUSTE' ? produto.stockAtual : 0)
    
    // Limpar erros quando mudar o tipo
    clearFieldError('quantidade')
  }

  const handleQuantidadeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const quantidade = parseInt(e.target.value) || 0
    setFieldValue('quantidade', quantidade)
    
    // Validação em tempo real para saídas
    if (values.tipo === 'SAIDA' && quantidade > produto.stockAtual) {
      setFieldError('quantidade', `Stock insuficiente. Disponível: ${produto.stockAtual}`)
    } else {
      clearFieldError('quantidade')
    }
  }

  const calcularNovoEstoque = () => {
    let novo = produto.stockAtual
    
    switch (values.tipo) {
      case 'ENTRADA':
        novo = produto.stockAtual + values.quantidade
        break
      case 'SAIDA':
        novo = produto.stockAtual - values.quantidade
        break
      case 'AJUSTE':
        novo = values.quantidade
        break
    }
    
    setNovoEstoque(Math.max(0, novo))
  }

  const getStatusNovoEstoque = () => {
    if (novoEstoque === 0) {
      return { color: 'red', label: 'Sem stock' }
    }
    if (novoEstoque <= produto.stockMinimo) {
      return { color: 'yellow', label: 'Stock baixo' }
    }
    return { color: 'green', label: 'Stock normal' }
  }

  const statusAtual = getStatusEstoque(produto)
  const statusNovo = getStatusNovoEstoque()
  const configTipo = values.tipo ? getTipoMovimentacaoConfig(values.tipo) : null

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informações do produto */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          {produto.nome}
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">SKU:</span>
            <span className="ml-2 font-mono">{produto.codigoInterno}</span>
          </div>
          <div>
            <span className="text-gray-500">Categoria:</span>
            <span className="ml-2">{typeof produto.categoria === 'object' && produto.categoria?.nome ? produto.categoria.nome : produto.categoria || 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-500">Stock Atual:</span>
            <span className={`ml-2 font-medium ${
              statusAtual.color === 'red' ? 'text-red-600' :
              statusAtual.color === 'yellow' ? 'text-yellow-600' :
              'text-green-600'
            }`}>
              {formatarQuantidade(produto.stockAtual)}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Stock Mínimo:</span>
            <span className="ml-2">{formatarQuantidade(produto.stockMinimo)}</span>
          </div>
        </div>
      </div>

      {/* Formulário de movimentação */}
      <div className="space-y-4">
        <Select
          label="Tipo de Movimentação *"
          value={values.tipo}
          onChange={handleTipoChange}
          options={[
            { value: '', label: 'Selecione o tipo de movimentação' },
            ...tiposMovimentacao.map(tipo => ({
              value: tipo.value,
              label: `${tipo.icon} ${tipo.label} - ${tipo.description}`
            }))
          ]}
          error={errors.tipo}
        />

        {configTipo && (
          <div className={`p-3 rounded border-l-4 ${
            configTipo.color === 'green' ? 'bg-green-50 border-green-400' :
            configTipo.color === 'red' ? 'bg-red-50 border-red-400' :
            'bg-blue-50 border-blue-400'
          }`}>
            <div className="flex items-center gap-2">
              <span className="text-lg">{configTipo.icon}</span>
              <div>
                <p className={`font-medium ${
                  configTipo.color === 'green' ? 'text-green-800' :
                  configTipo.color === 'red' ? 'text-red-800' :
                  'text-blue-800'
                }`}>
                  {configTipo.label}
                </p>
                <p className={`text-sm ${
                  configTipo.color === 'green' ? 'text-green-600' :
                  configTipo.color === 'red' ? 'text-red-600' :
                  'text-blue-600'
                }`}>
                  {configTipo.description}
                </p>
              </div>
            </div>
          </div>
        )}

        <Input
          label={values.tipo === 'AJUSTE' ? 'Novo Stock *' : 'Quantidade *'}
          type="number"
          min="0"
          placeholder={values.tipo === 'AJUSTE' ? 'Digite o novo valor do stock' : 'Digite a quantidade'}
          value={values.quantidade}
          onChange={handleQuantidadeChange}
          error={errors.quantidade}
          hint={
            values.tipo === 'AJUSTE'
              ? 'Digite o valor total que o stock deve ter'
              : values.tipo === 'SAIDA'
                ? `Disponível: ${formatarQuantidade(produto.stockAtual)}`
                : undefined
          }
        />

        <TextArea
          label="Observações *"
          placeholder="Digite o motivo da movimentação..."
          rows={3}
          {...getFieldProps('observacoes')}
        />
      </div>

      {/* Preview do resultado */}
      {values.tipo && values.quantidade > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">Resultado da Movimentação</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-blue-700">Stock Atual:</span>
              <span className="ml-2 font-medium">{formatarQuantidade(produto.stockAtual)}</span>
            </div>
            <div>
              <span className="text-blue-700">Novo Stock:</span>
              <span className={`ml-2 font-medium ${
                statusNovo.color === 'red' ? 'text-red-600' :
                statusNovo.color === 'yellow' ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {formatarQuantidade(novoEstoque)}
              </span>
            </div>
            {values.tipo !== 'AJUSTE' && (
              <div>
                <span className="text-blue-700">Variação:</span>
                <span className={`ml-2 font-medium ${
                  values.tipo === 'ENTRADA' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {values.tipo === 'ENTRADA' ? '+' : '-'}{formatarQuantidade(values.quantidade)}
                </span>
              </div>
            )}
            <div>
              <span className="text-blue-700">Status:</span>
              <span className={`ml-2 font-medium ${
                statusNovo.color === 'red' ? 'text-red-600' :
                statusNovo.color === 'yellow' ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {statusNovo.label}
              </span>
            </div>
          </div>
          
          {produto.precoCusto > 0 && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <div className="text-sm text-blue-700">
                <div>Valor da movimentação: {formatarValor(values.quantidade * produto.precoCusto)}</div>
                <div>Valor total em stock: {formatarValor(novoEstoque * produto.precoCusto)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Avisos */}
      {values.tipo === 'SAIDA' && values.quantidade > 0 && novoEstoque <= produto.stockMinimo && (
        <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">
              Atenção: Esta movimentação deixará o stock abaixo do mínimo
            </span>
          </div>
        </div>
      )}

      {novoEstoque === 0 && (
        <div className="bg-red-50 p-3 rounded border border-red-200">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-800">
              Atenção: Esta movimentação zerará o stock do produto
            </span>
          </div>
        </div>
      )}

      {/* Ações */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={!values.tipo || !values.quantidade || !values.observacoes}
          leftIcon={
            values.tipo === 'ENTRADA' ? <TrendingUp className="h-4 w-4" /> :
            values.tipo === 'SAIDA' ? <TrendingDown className="h-4 w-4" /> :
            <Settings className="h-4 w-4" />
          }
        >
          {configTipo ? `Registrar ${configTipo.label}` : 'Registrar Movimentação'}
        </Button>
      </div>
    </form>
  )
}

export default MovimentacaoForm